import {
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { memo, useCallback } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  EdiOutboundPayloadDestination,
  type TenediPartnershipFieldsFragment,
} from "../../../generated/graphql";
import { DESTINATION_LABELS } from "../tenedi-connection-copy";
import { benchLinkFor } from "../tenedi-connection-links";

const styles = {
  paper: { p: 2 } satisfies SxProps<Theme>,
  header: {
    justifyContent: "space-between",
    alignItems: "center",
  } satisfies SxProps<Theme>,
  addActions: { alignItems: "center" } satisfies SxProps<Theme>,
} as const;

/** "Accepts automatically · replies to 204" and the like, for the table. */
export const replies990Summary = (
  partnership: Pick<
    TenediPartnershipFieldsFragment,
    "autoAcceptOrders" | "send990InResponseTo204" | "send990InResponseTo211"
  >,
): string => {
  const replies = [
    partnership.send990InResponseTo204 ? "204" : null,
    partnership.send990InResponseTo211 ? "211" : null,
  ].filter((value): value is string => value !== null);
  const accept = partnership.autoAcceptOrders
    ? "Accepts automatically"
    : "Staff accept";
  return replies.length === 0
    ? `${accept} · no 990 replies`
    : `${accept} · replies to ${replies.join(" and ")}`;
};

type PartnershipRowProps = {
  readonly partnership: TenediPartnershipFieldsFragment;
  readonly onEdit: (partnership: TenediPartnershipFieldsFragment) => void;
};

const PartnershipRow = memo(({ partnership, onEdit }: PartnershipRowProps) => {
  const handleEdit = useCallback(() => {
    onEdit(partnership);
  }, [onEdit, partnership]);
  const sendsToTenedi =
    partnership.ediOutboundPayloadDestination ===
    EdiOutboundPayloadDestination.Tenedi;

  return (
    <TableRow>
      <TableCell>
        {partnership.companyStandardCarrierAlphaCode ?? "No SCAC"}
      </TableCell>
      <TableCell>
        {DESTINATION_LABELS[partnership.ediOutboundPayloadDestination]}
      </TableCell>
      <TableCell>{partnership.edi214ShipmentStatuses.length}</TableCell>
      <TableCell>{replies990Summary(partnership)}</TableCell>
      <TableCell align="right">
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button size="small" onClick={handleEdit}>
            Edit
          </Button>
          <Tooltip
            title={
              sendsToTenedi
                ? "Open the outbound test bench on this partnership."
                : "The bench runs Tenedi partnerships only."
            }
          >
            <span>
              <Button
                component={RouterLink}
                disabled={!sendsToTenedi}
                size="small"
                to={benchLinkFor(partnership.uuid)}
              >
                Test in bench
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </TableCell>
    </TableRow>
  );
});
PartnershipRow.displayName = "PartnershipRow";

type PartnershipsTableProps = {
  readonly partnerships: readonly TenediPartnershipFieldsFragment[];
  readonly addDisabled: boolean;
  /** Why Add is disabled, shown beside it; null shows nothing. */
  readonly addHint: string | null;
  readonly onAdd: () => void;
  readonly onEdit: (partnership: TenediPartnershipFieldsFragment) => void;
};

/** One row per partnership for this carrier and customer. */
const PartnershipsTable = ({
  partnerships,
  addDisabled,
  addHint,
  onAdd,
  onEdit,
}: PartnershipsTableProps) => (
  <Paper sx={styles.paper} variant="outlined">
    <Stack direction="row" sx={styles.header}>
      <Typography variant="h6">Partnerships</Typography>
      <Stack direction="row" spacing={2} sx={styles.addActions}>
        {addHint !== null && (
          <Typography color="error" variant="body2">
            {addHint}
          </Typography>
        )}
        <Button disabled={addDisabled} variant="contained" onClick={onAdd}>
          Add Tenedi partnership
        </Button>
      </Stack>
    </Stack>
    {partnerships.length === 0 ? (
      <Typography color="text.secondary" variant="body2">
        No partnership between this carrier and customer yet.
      </Typography>
    ) : (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>SCAC</TableCell>
            <TableCell>Sends to</TableCell>
            <TableCell>Status updates (214)</TableCell>
            <TableCell>Tenders and 990s</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {partnerships.map((partnership) => (
            <PartnershipRow
              key={partnership.uuid}
              partnership={partnership}
              onEdit={onEdit}
            />
          ))}
        </TableBody>
      </Table>
    )}
  </Paper>
);

export default PartnershipsTable;
